import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./MapLayersPanel.css";
import type { MapSavedSetListItem, MapSavedSetDetails } from "../../api/map-saved-sets/types";
import type { ElectoralMetadataFieldListItem } from "../../api/electoral-metadata/types";
import type { AssignmentsResponse } from "../../api/map/types";

/** 9 muted green levels for legend (must match ElectoralDistrictsLayer METADATA_LEVEL_COLORS) */
const LEGEND_LEVEL_COLORS = ["#f5f9f4", "#e5efe2", "#cce0c7", "#a8c9a0", "#7bb06f", "#4d9645", "#2d7a2a", "#1a5f1a", "#0d450d"];

/** Format electoral district for list: name only (no code), fallback to code then id. */
function formatElectoralDistrictDisplay(d: { electoralDistrictId: string; name?: string | null; code?: string | null }): string {
    const name = d.name?.trim();
    const code = d.code?.trim();
    if (name) return name;
    if (code) return code;
    return d.electoralDistrictId;
}

export interface MunicipalityOption {
    id: string;
    name: string;
    code: string;
}

export interface CountyOption {
    id: string;
    name: string;
    code: string;
}

export interface TemporaryPostalCodeSelectionItem {
    postalCodePolygonId: string;
    postalCode?: string;
    locality?: string | null;
    householdCount?: number | null;
}

export interface TemporaryElectoralDistrictSelectionItem {
    electoralDistrictId: string;
    name?: string;
    code?: string;
    householdCount?: number | null;
}

export interface MapLayersPanelProps {
    showPostalCodes: boolean;
    showElectoralDistricts: boolean;
    showMunicipalities: boolean;
    showAssignments?: boolean;
    /** Current assignments data for displaying summary list below the toggle */
    assignmentsData?: AssignmentsResponse | null;
    /** When true, the assignments toggle is disabled (e.g. no municipality selected yet) */
    assignmentsNeedsMunicipality?: boolean;
    postalCodesFilter?: string;
    electoralDistrictsFilter?: string;
    municipalitiesFilter?: string;
    onTogglePostalCodes: (show: boolean) => void;
    onToggleElectoralDistricts: (show: boolean) => void;
    onToggleMunicipalities: (show: boolean) => void;
    onToggleAssignments?: (show: boolean) => void;
    onPostalCodesFilterChange: (filter: string) => void;
    onElectoralDistrictsFilterChange: (filter: string) => void;
    onMunicipalitiesFilterChange: (filter: string) => void;
    /** County (län) dropdown above municipality (optional) */
    countyOptions?: CountyOption[];
    selectedCountyId?: string | null;
    onCountyChange?: (id: string | null) => void;
    countiesLoading?: boolean;
    countiesError?: Error | null;
    /** Access-scoped municipality dropdown (optional) */
    municipalityOptions?: MunicipalityOption[];
    selectedMunicipalityId?: string | null;
    onMunicipalityChange?: (id: string | null) => void;
    municipalitiesLoading?: boolean;
    municipalitiesError?: Error | null;
    /** Saved set selector and CRUD (optional) */
    savedSets?: MapSavedSetListItem[];
    selectedSavedSetId?: string | null;
    onSavedSetChange?: (id: string | null) => void;
    savedSetsLoading?: boolean;
    savedSetsError?: Error | null;
    savedSetDetails?: MapSavedSetDetails | null;
    savedSetDetailsLoading?: boolean;
    onDeleteSavedSet?: (id: string) => void;
    onCreateSavedSet?: () => void;
    onUpdateSavedSet?: (id: string) => void;
    /** When false, create/update/delete and click-to-toggle are hidden (KretsAdmin+ required) */
    canEditSavedSets?: boolean;
    /** When false, hide entire saved sets section (e.g. KretsAdmin/Distributor viewing another municipality) */
    showSavedSetsSection?: boolean;
    /** Enable/disable click on map for postal codes (when saved set is selected) */
    clickPostalCodesEnabled?: boolean;
    /** Call with no args to toggle, or (enabled: boolean) to set */
    onClickPostalCodesEnabledChange?: (enabled?: boolean) => void;
    /** Enable/disable click on map for electoral districts (when saved set is selected) */
    clickElectoralDistrictsEnabled?: boolean;
    onClickElectoralDistrictsEnabledChange?: (enabled?: boolean) => void;
    temporarySelectedPostalCodes?: TemporaryPostalCodeSelectionItem[];
    temporarySelectedElectoralDistricts?: TemporaryElectoralDistrictSelectionItem[];
    /** Electoral metadata field list for map choropleth dropdown (Decimal fields only in UI) */
    electoralMetadataFields?: ElectoralMetadataFieldListItem[];
    /** Field IDs that have at least one value for the selected municipality (only these shown in dropdown) */
    metadataFieldIdsWithValues?: string[];
    /** Selected municipality ID for metadata (used for link to fill in metadata) */
    metadataMunicipalityId?: string | null;
    /** True while grid is loading to know if we have no data yet vs no metadata values */
    metadataGridLoading?: boolean;
    /** Error when grid failed to load (e.g. 403) – show message so user knows metadata could not be loaded */
    metadataGridError?: Error | null;
    metadataFieldIdForMap?: string | null;
    onMetadataFieldForMapChange?: (fieldId: string | null) => void;
    /** When true, valdistrikt fill uses metadata choropleth; when false, default purple (easy compare with valda distrikt) */
    showMetadataOnMap?: boolean;
    onShowMetadataOnMapChange?: (show: boolean) => void;
    /** When false, metadata dropdown is disabled and shows "Välj kommun för att visa metadata" */
    hasMetadataMunicipality?: boolean;
    /** Min/max for legend when a metadata field is selected */
    electoralMetadataMinMax?: { min: number; max: number };
}

export const MapLayersPanel = ({
    showPostalCodes,
    showElectoralDistricts,
    showMunicipalities,
    showAssignments,
    assignmentsData,
    assignmentsNeedsMunicipality,
    postalCodesFilter,
    electoralDistrictsFilter,
    municipalitiesFilter,
    onTogglePostalCodes,
    onToggleElectoralDistricts,
    onToggleMunicipalities,
    onToggleAssignments,
    onPostalCodesFilterChange,
    onElectoralDistrictsFilterChange,
    onMunicipalitiesFilterChange,
    countyOptions,
    selectedCountyId = null,
    onCountyChange,
    countiesLoading = false,
    countiesError = null,
    municipalityOptions,
    selectedMunicipalityId = null,
    onMunicipalityChange,
    municipalitiesLoading = false,
    municipalitiesError = null,
    savedSets = [],
    selectedSavedSetId = null,
    onSavedSetChange,
    savedSetsLoading = false,
    savedSetsError = null,
    savedSetDetails = null,
    savedSetDetailsLoading = false,
    onDeleteSavedSet,
    onCreateSavedSet,
    onUpdateSavedSet,
    canEditSavedSets = true,
    showSavedSetsSection: showSavedSetsSectionProp,
    clickPostalCodesEnabled = false,
    onClickPostalCodesEnabledChange,
    clickElectoralDistrictsEnabled = false,
    onClickElectoralDistrictsEnabledChange,
    temporarySelectedPostalCodes = [],
    temporarySelectedElectoralDistricts = [],
    electoralMetadataFields = [],
    metadataFieldIdsWithValues = [],
    metadataMunicipalityId = null,
    metadataGridLoading = false,
    metadataGridError = null,
    metadataFieldIdForMap = null,
    onMetadataFieldForMapChange,
    showMetadataOnMap = false,
    onShowMetadataOnMapChange,
    hasMetadataMunicipality = false,
    electoralMetadataMinMax = { min: 0, max: 100 }
}: MapLayersPanelProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

    const showMunicipalitySelector = onMunicipalityChange !== undefined;
    const showSavedSetsSection =
        (showSavedSetsSectionProp !== undefined ? showSavedSetsSectionProp : onSavedSetChange !== undefined) &&
        onSavedSetChange !== undefined;
    /** Set to true to show postal/electoral filter inputs (hidden for now) */
    const showLayerFilters = false;
    const showSavedSetLists =
        showSavedSetsSection &&
        selectedSavedSetId &&
        (savedSetDetails !== undefined || savedSetDetailsLoading);
    const showTemporarySelectionLists = showSavedSetsSection && !selectedSavedSetId;

    const handleDeleteClick = (id: string) => {
        setDeleteConfirmId(id);
    };

    const handleDeleteConfirm = () => {
        if (deleteConfirmId && onDeleteSavedSet) {
            onDeleteSavedSet(deleteConfirmId);
            setDeleteConfirmId(null);
            onSavedSetChange?.(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmId(null);
    };

    return (
        <div className="map-layers-panel">
            <h3 className="map-layers-panel__title">{t("map.layers.title", "Kartlager")}</h3>

            {/* County (län) and municipality dropdowns */}
            {showMunicipalitySelector && (
                <div className="map-layers-panel__section" role="region" aria-label={t("map.layers.countyAndMunicipalityLabel", "County and municipality")}>
                    <h4 className="map-layers-panel__group-title">{t("map.layers.groupArea", "Område")}</h4>
                    {onCountyChange && (
                        <>
                            <label className="map-layers-panel__filter-label" id="map-layers-county-label">
                                {t("map.layers.countyLabel", "County")}
                            </label>
                            {countiesLoading && (
                                <p className="map-layers-panel__loading" aria-live="polite">
                                    {t("map.layers.countiesLoading", "Loading counties...")}
                                </p>
                            )}
                            {countiesError && (
                                <p className="map-layers-panel__error" role="alert">
                                    {t("map.layers.countiesError", "Failed to load counties")}
                                </p>
                            )}
                            {!countiesLoading && !countiesError && (
                                <select
                                    className="map-layers-panel__select"
                                    value={selectedCountyId ?? ""}
                                    onChange={(e) => onCountyChange(e.target.value || null)}
                                    aria-labelledby="map-layers-county-label"
                                    aria-label={t("map.layers.countyLabel", "County")}
                                >
                                    <option value="">{t("map.layers.countyPlaceholder", "Select county")}</option>
                                    {countyOptions?.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </>
                    )}
                    <label className="map-layers-panel__filter-label" id="map-layers-municipality-label">
                        {t("map.layers.municipalityLabel", "Municipality")}
                    </label>
                    {municipalitiesLoading && (
                        <p className="map-layers-panel__loading" aria-live="polite">
                            {t("map.layers.municipalitiesLoading", "Loading municipalities...")}
                        </p>
                    )}
                    {municipalitiesError && (
                        <p className="map-layers-panel__error" role="alert">
                            {t("map.layers.municipalitiesError", "Failed to load municipalities")}
                        </p>
                    )}
                    {!municipalitiesLoading && !municipalitiesError && (
                        <select
                            className="map-layers-panel__select"
                            value={selectedMunicipalityId ?? ""}
                            onChange={(e) => onMunicipalityChange(e.target.value || null)}
                            aria-labelledby="map-layers-municipality-label"
                            aria-label={t("map.layers.municipalityLabel", "Municipality")}
                        >
                            <option value="">{t("map.layers.municipalityPlaceholder", "Select municipality")}</option>
                            {municipalityOptions?.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.name} ({m.code})
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            )}

            {/* Saved sets section */}
            {showSavedSetsSection && (
                <div className="map-layers-panel__group">
                <div className="map-layers-panel__section" role="region" aria-label={t("map.layers.savedSetsTitle", "Saved sets")}>
                    <h4 className="map-layers-panel__group-title">{t("map.layers.groupSavedSets", "Sparade uppsättningar")}</h4>
                    <label className="map-layers-panel__filter-label" id="map-layers-savedset-label">
                        {t("map.layers.savedSetLabel", "Saved set")}
                    </label>
                    {savedSetsLoading && (
                        <p className="map-layers-panel__loading" aria-live="polite">
                            {t("map.layers.savedSetsLoading", "Loading saved sets...")}
                        </p>
                    )}
                    {savedSetsError && (
                        <p className="map-layers-panel__error" role="alert">
                            {t("map.layers.savedSetsError", "Failed to load saved sets")}
                        </p>
                    )}
                    {!savedSetsLoading && !savedSetsError && (
                        <>
                            <select
                                className="map-layers-panel__select"
                                value={selectedSavedSetId ?? ""}
                                onChange={(e) => onSavedSetChange(e.target.value || null)}
                                aria-labelledby="map-layers-savedset-label"
                                aria-label={t("map.layers.savedSetLabel", "Saved set")}
                            >
                                <option value="">{t("map.layers.savedSetPlaceholder", "Select saved set")}</option>
                                {savedSets?.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                            {canEditSavedSets && (
                                <div className="map-layers-panel__saved-set-actions">
                                    <button
                                        type="button"
                                        className="map-layers-panel__btn map-layers-panel__btn--primary"
                                        aria-label={t("map.layers.createSavedSet", "Create new")}
                                        onClick={() => onCreateSavedSet?.()}
                                    >
                                        {t("map.layers.createSavedSet", "Create new")}
                                    </button>
                                    {selectedSavedSetId && (
                                        <>
                                            <button
                                                type="button"
                                                className="map-layers-panel__btn"
                                                aria-label={t("map.layers.updateSavedSet", "Update")}
                                                onClick={() => onUpdateSavedSet?.(selectedSavedSetId)}
                                            >
                                                {t("map.layers.updateSavedSet", "Update")}
                                            </button>
                                            <button
                                                type="button"
                                                className="map-layers-panel__btn map-layers-panel__btn--danger"
                                                aria-label={t("map.layers.deleteSavedSet", "Delete")}
                                                onClick={() => handleDeleteClick(selectedSavedSetId)}
                                            >
                                                {t("map.layers.deleteSavedSet", "Delete")}
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                            {canEditSavedSets && onClickPostalCodesEnabledChange && onClickElectoralDistrictsEnabledChange && (
                                <>
                                    <div className="map-layers-panel__click-toggles" role="group" aria-label={t("map.layers.clickOnMapLabel", "Click on map")}>
                                        <button
                                            type="button"
                                            className={`map-layers-panel__btn map-layers-panel__btn--toggle ${clickPostalCodesEnabled ? "map-layers-panel__btn--toggle-active" : ""}`}
                                            aria-pressed={clickPostalCodesEnabled}
                                            onClick={() => onClickPostalCodesEnabledChange()}
                                        >
                                            {t("map.layers.clickPostalCodesEnabledShort", "Postnummer")}
                                        </button>
                                        <button
                                            type="button"
                                            className={`map-layers-panel__btn map-layers-panel__btn--toggle ${clickElectoralDistrictsEnabled ? "map-layers-panel__btn--toggle-active" : ""}`}
                                            aria-pressed={clickElectoralDistrictsEnabled}
                                            onClick={() => onClickElectoralDistrictsEnabledChange()}
                                        >
                                            {t("map.layers.clickElectoralDistrictsEnabledShort", "Valdistrikt")}
                                        </button>
                                    </div>
                                    {!selectedSavedSetId && (
                                        <p className="map-layers-panel__muted">
                                            {t("map.layers.temporarySelectionHint", "Testläge: valen sparas inte utan vald uppsättning.")}
                                        </p>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* Selected postal codes and electoral districts */}
                    {showSavedSetLists && (
                        <>
                            {savedSetDetailsLoading && (
                                <p className="map-layers-panel__loading" aria-live="polite">
                                    {t("map.layers.savedSetsLoading", "Loading...")}
                                </p>
                            )}
                            {!savedSetDetailsLoading && savedSetDetails && (
                                <>
                                    {showPostalCodes && (
                                        <p className="map-layers-panel__hint map-layers-panel__muted">
                                            {t("map.layers.clickMapToToggleSelection", "Click a postal code or electoral district on the map to add or remove it from the set.")}
                                        </p>
                                    )}
                                    <details className="map-layers-panel__list-section">
                                        <summary id="map-layers-postal-codes-heading" className="map-layers-panel__list-heading map-layers-panel__summary">
                                            {savedSetDetails.postalCodes.length === 0
                                                ? t("map.layers.selectedPostalCodes", "Selected postal codes")
                                                : t("map.layers.selectedPostalCodesWithCount", "Selected postal codes ({{count}}, {{households}} households)", {
                                                      count: savedSetDetails.postalCodes.length,
                                                      households: savedSetDetails.postalCodes
                                                      .reduce((sum, pc) => sum + (pc.householdCount ?? 0), 0)
                                                      .toLocaleString(i18n.language === "sv" ? "sv-SE" : "en-GB")
                                                  })}
                                        </summary>
                                        <ul className="map-layers-panel__list">
                                            {savedSetDetails.postalCodes.length === 0 ? (
                                                <li>{t("map.layers.noSavedSetSelected", "None selected")}</li>
                                            ) : (
                                                savedSetDetails.postalCodes.map((pc) => (
                                                    <li key={pc.postalCodePolygonId}>
                                                        {pc.postalCode || pc.postalCodePolygonId}
                                                        {pc.locality ? ` (${pc.locality})` : ""}
                                                        {pc.householdCount != null && (
                                                            <span className="map-layers-panel__household-count">
                                                                {" "}({t("map.layers.householdCount", "Households")}: {pc.householdCount})
                                                            </span>
                                                        )}
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    </details>
                                    <details className="map-layers-panel__list-section">
                                        <summary id="map-layers-electoral-heading" className="map-layers-panel__list-heading map-layers-panel__summary">
                                            {savedSetDetails.priorityElectoralDistricts.length === 0
                                                ? t("map.layers.selectedElectoralDistricts", "Priority electoral districts")
                                                : t("map.layers.selectedElectoralDistrictsWithCount", "Priority electoral districts ({{count}}, {{households}} households)", {
                                                      count: savedSetDetails.priorityElectoralDistricts.length,
                                                      households: savedSetDetails.priorityElectoralDistricts
                                                          .reduce((sum, d) => sum + (d.householdCount ?? 0), 0)
                                                          .toLocaleString(i18n.language === "sv" ? "sv-SE" : "en-GB")
                                                  })}
                                        </summary>
                                        <ul className="map-layers-panel__list">
                                            {savedSetDetails.priorityElectoralDistricts.length === 0 ? (
                                                <li>{t("map.layers.noSavedSetSelected", "None selected")}</li>
                                            ) : (
                                                savedSetDetails.priorityElectoralDistricts.map((d) => (
                                                    <li key={d.electoralDistrictId}>
                                                        {formatElectoralDistrictDisplay(d)}
                                                        {d.householdCount != null && (
                                                            <span className="map-layers-panel__household-count">
                                                                {" "}({t("map.layers.householdCount", "Households")}: {d.householdCount})
                                                            </span>
                                                        )}
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    </details>
                                </>
                            )}
                            {!savedSetDetailsLoading && !savedSetDetails && selectedSavedSetId && (
                                <p className="map-layers-panel__muted">{t("map.layers.noSavedSetSelected", "Select a saved set to see selections")}</p>
                            )}
                        </>
                    )}
                    {showTemporarySelectionLists && (
                        <>
                            <details className="map-layers-panel__list-section">
                                <summary id="map-layers-temp-postal-codes-heading" className="map-layers-panel__list-heading map-layers-panel__summary">
                                    {temporarySelectedPostalCodes.length === 0
                                        ? t("map.layers.selectedPostalCodes", "Selected postal codes")
                                        : t("map.layers.selectedPostalCodesWithCount", "Selected postal codes ({{count}}, {{households}} households)", {
                                              count: temporarySelectedPostalCodes.length,
                                              households: temporarySelectedPostalCodes
                                                  .reduce((sum, pc) => sum + (pc.householdCount ?? 0), 0)
                                                  .toLocaleString(i18n.language === "sv" ? "sv-SE" : "en-GB")
                                          })}
                                </summary>
                                <ul className="map-layers-panel__list">
                                    {temporarySelectedPostalCodes.length === 0 ? (
                                        <li>{t("map.layers.noSavedSetSelected", "None selected")}</li>
                                    ) : (
                                        temporarySelectedPostalCodes.map((pc) => (
                                            <li key={pc.postalCodePolygonId}>
                                                {pc.postalCode || pc.postalCodePolygonId}
                                                {pc.locality ? ` (${pc.locality})` : ""}
                                                {pc.householdCount != null && (
                                                    <span className="map-layers-panel__household-count">
                                                        {" "}({t("map.layers.householdCount", "Households")}: {pc.householdCount})
                                                    </span>
                                                )}
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </details>
                            <details className="map-layers-panel__list-section">
                                <summary id="map-layers-temp-electoral-heading" className="map-layers-panel__list-heading map-layers-panel__summary">
                                    {temporarySelectedElectoralDistricts.length === 0
                                        ? t("map.layers.selectedElectoralDistricts", "Priority electoral districts")
                                        : t("map.layers.selectedElectoralDistrictsWithCount", "Priority electoral districts ({{count}}, {{households}} households)", {
                                              count: temporarySelectedElectoralDistricts.length,
                                              households: temporarySelectedElectoralDistricts
                                                  .reduce((sum, d) => sum + (d.householdCount ?? 0), 0)
                                                  .toLocaleString(i18n.language === "sv" ? "sv-SE" : "en-GB")
                                          })}
                                </summary>
                                <ul className="map-layers-panel__list">
                                    {temporarySelectedElectoralDistricts.length === 0 ? (
                                        <li>{t("map.layers.noSavedSetSelected", "None selected")}</li>
                                    ) : (
                                        temporarySelectedElectoralDistricts.map((d) => (
                                            <li key={d.electoralDistrictId}>
                                                {formatElectoralDistrictDisplay(d)}
                                                {d.householdCount != null && (
                                                    <span className="map-layers-panel__household-count">
                                                        {" "}({t("map.layers.householdCount", "Households")}: {d.householdCount})
                                                    </span>
                                                )}
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </details>
                        </>
                    )}
                    {showAssignments && assignmentsData && assignmentsData.features.length > 0 && (
                        <details className="map-layers-panel__list-section">
                            <summary className="map-layers-panel__list-heading map-layers-panel__summary">
                                {t("map.layers.assignmentsWithCount", "Uppdrag ({{count}}, {{addresses}} adresser)", {
                                    count: assignmentsData.features.length,
                                    addresses: (assignmentsData.metadata.totalTargetAddresses ?? 0)
                                        .toLocaleString(i18n.language === "sv" ? "sv-SE" : "en-GB")
                                })}
                            </summary>
                            <ul className="map-layers-panel__list">
                                {assignmentsData.features.map((f) => (
                                    <li key={f.properties.assignmentId}>
                                        {f.properties.name}
                                        {f.properties.targetAddressCount != null && (
                                            <span className="map-layers-panel__household-count">
                                                {" "}({t("map.layers.addressCount", "Adresser")}: {f.properties.targetAddressCount.toLocaleString(i18n.language === "sv" ? "sv-SE" : "en-GB")})
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
                </div>
            )}

            {/* Delete confirmation dialog */}
            {deleteConfirmId && (
                <div
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="map-layers-delete-dialog-title"
                    aria-describedby="map-layers-delete-dialog-desc"
                    className="map-layers-panel__dialog"
                >
                    <h3 id="map-layers-delete-dialog-title">
                        {t("map.layers.deleteConfirmTitle", "Confirm delete")}
                    </h3>
                    <p id="map-layers-delete-dialog-desc">
                        {t("map.layers.deleteConfirmMessage", "Are you sure you want to delete this saved set?")}
                    </p>
                    <div className="map-layers-panel__dialog-actions">
                        <button
                            type="button"
                            className="map-layers-panel__btn"
                            onClick={handleDeleteCancel}
                        >
                            {t("map.layers.deleteConfirmCancel", "Cancel")}
                        </button>
                        <button
                            type="button"
                            className="map-layers-panel__btn map-layers-panel__btn--danger"
                            onClick={handleDeleteConfirm}
                        >
                            {t("map.layers.deleteConfirmConfirm", "Delete")}
                        </button>
                    </div>
                </div>
            )}

            {/* Layer toggles: municipalities, postal codes, electoral districts */}
            <div className="map-layers-panel__group">
                <h4 className="map-layers-panel__group-title">{t("map.layers.groupLayers", "Lager")}</h4>
            {/* Municipalities Section – över postnummer */}
            <div className="map-layers-panel__section">
                <div className="map-layers-panel__toggle-row">
                    <label className="map-layers-panel__label">
                        <input
                            type="checkbox"
                            checked={showMunicipalities}
                            onChange={(e) => onToggleMunicipalities(e.target.checked)}
                            className="map-layers-panel__checkbox"
                        />
                        <span>{t("map.layers.municipalities", "Visa kommuner")}</span>
                    </label>
                </div>
                {showMunicipalities && false && (
                    <div className="map-layers-panel__filter">
                        <label className="map-layers-panel__filter-label">
                            {t("map.layers.municipalitiesFilter", "Filtrera kommuner (kommaseparerade koder)")}
                        </label>
                        <input
                            type="text"
                            value={municipalitiesFilter || ""}
                            onChange={(e) => onMunicipalitiesFilterChange(e.target.value)}
                            placeholder={t("map.layers.municipalitiesFilterPlaceholder", "t.ex. 0180,0181 eller lämna tomt för alla")}
                            className="map-layers-panel__input"
                        />
                    </div>
                )}
            </div>

            {onToggleAssignments !== undefined && (
                <div className="map-layers-panel__section">
                    <div className="map-layers-panel__toggle-row">
                        <label
                            className="map-layers-panel__label"
                            title={assignmentsNeedsMunicipality ? t("map.layers.assignmentsNeedsMunicipality", "Välj en kommun för att visa uppdrag") : undefined}
                        >
                            <input
                                type="checkbox"
                                checked={showAssignments ?? false}
                                onChange={(e) => onToggleAssignments(e.target.checked)}
                                className="map-layers-panel__checkbox"
                                disabled={!!assignmentsNeedsMunicipality}
                            />
                            <span>{t("map.layers.assignments", "Visa uppdrag")}</span>
                        </label>
                    </div>
                </div>
            )}

            {/* Postal Codes Section */}
            <div className="map-layers-panel__section">
                <div className="map-layers-panel__toggle-row">
                    <label className="map-layers-panel__label">
                        <input
                            type="checkbox"
                            checked={showPostalCodes}
                            onChange={(e) => onTogglePostalCodes(e.target.checked)}
                            className="map-layers-panel__checkbox"
                        />
                        <span>{t("map.layers.postalCodes", "Visa postnummer")}</span>
                    </label>
                </div>
                {showPostalCodes && showLayerFilters && (
                    <div className="map-layers-panel__filter">
                        <label className="map-layers-panel__filter-label">
                            {t("map.layers.postalCodesFilter", "Filtrera postnummer (kommaseparerade)")}
                        </label>
                        <input
                            type="text"
                            value={postalCodesFilter || ""}
                            onChange={(e) => onPostalCodesFilterChange(e.target.value)}
                            placeholder={t("map.layers.postalCodesFilterPlaceholder", "t.ex. 11430,11431 eller lämna tomt för alla")}
                            className="map-layers-panel__input"
                        />
                    </div>
                )}
            </div>

            {/* Electoral Districts Section */}
            <div className="map-layers-panel__section">
                <div className="map-layers-panel__toggle-row">
                    <label className="map-layers-panel__label">
                        <input
                            type="checkbox"
                            checked={showElectoralDistricts}
                            onChange={(e) => onToggleElectoralDistricts(e.target.checked)}
                            className="map-layers-panel__checkbox"
                        />
                        <span>{t("map.layers.electoralDistricts", "Visa valdistrikt")}</span>
                    </label>
                </div>
                {showElectoralDistricts && showLayerFilters && (
                    <div className="map-layers-panel__filter">
                        <label className="map-layers-panel__filter-label">
                            {t("map.layers.electoralDistrictsFilter", "Filtrera valdistrikt (kommaseparerade)")}
                        </label>
                        <input
                            type="text"
                            value={electoralDistrictsFilter || ""}
                            onChange={(e) => onElectoralDistrictsFilterChange(e.target.value)}
                            placeholder={t("map.layers.electoralDistrictsFilterPlaceholder", "t.ex. ABC123,DEF456 eller lämna tomt för alla")}
                            className="map-layers-panel__input"
                        />
                    </div>
                )}
            </div>
            </div>

            {/* Metadata section – visas för vald kommun (alla roller inkl. KretsAdmin/utdelare kan se andra kommuners metadata) */}
            {onMetadataFieldForMapChange !== undefined && hasMetadataMunicipality && (
                <div className="map-layers-panel__group">
                <div className="map-layers-panel__section map-layers-panel__metadata-section">
                    <h4 className="map-layers-panel__group-title">{t("map.layers.groupElectoralMetadata", "Valdistriktsmetadata")}</h4>
                    <div className="map-layers-panel__toggle-row">
                        <label className="map-layers-panel__label">
                            <input
                                type="checkbox"
                                checked={showMetadataOnMap}
                                onChange={(e) => onShowMetadataOnMapChange?.(e.target.checked)}
                                className="map-layers-panel__checkbox"
                            />
                            <span>{t("map.layers.showMetadata", "Visa metadata")}</span>
                        </label>
                    </div>
                    {showMetadataOnMap && (
                        <>
                            {metadataGridLoading && (
                                <p className="map-layers-panel__loading map-layers-panel__muted" aria-live="polite">
                                    {t("map.layers.metadataLoading", "Laddar metadata...")}
                                </p>
                            )}
                            {!metadataGridLoading && metadataGridError && (
                                <p className="map-layers-panel__error map-layers-panel__muted" role="alert">
                                    {t("map.layers.metadataLoadError", "Kunde inte ladda metadata för denna kommun. Kontrollera att du har behörighet.")}
                                </p>
                            )}
                            {!metadataGridLoading && !metadataGridError && metadataFieldIdsWithValues.length === 0 && metadataMunicipalityId && (
                                <Link
                                    to={`/admin/metadata-grid?municipalityId=${encodeURIComponent(metadataMunicipalityId)}`}
                                    className="map-layers-panel__metadata-fill-link"
                                >
                                    {t("map.layers.fillMetadataLink", "Fyll i metadata för denna kommun")}
                                </Link>
                            )}
                            {!metadataGridLoading && !metadataGridError && metadataFieldIdsWithValues.length > 0 && (
                                <>
                                    <label className="map-layers-panel__filter-label" htmlFor="map-layers-metadata-field">
                                        {t("map.layers.metadataFieldLabel", "Metadatafält")}
                                    </label>
                                    <select
                                        id="map-layers-metadata-field"
                                        className="map-layers-panel__select"
                                        value={metadataFieldIdForMap ?? ""}
                                        onChange={(e) => onMetadataFieldForMapChange(e.target.value || null)}
                                        aria-label={t("map.layers.metadataFieldLabel", "Metadatafält")}
                                    >
                                        <option value="">{t("map.layers.metadataFieldPlaceholder", "Välj fält")}</option>
                                        {electoralMetadataFields
                                            .filter((f) => f.type === "Decimal" && f.isActive && metadataFieldIdsWithValues.includes(f.id))
                                            .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
                                            .map((f) => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name}
                                                </option>
                                            ))}
                                    </select>
                                    {metadataFieldIdForMap && (
                                        <div className="map-layers-panel__legend" role="img" aria-label={t("map.layers.metadataLegendLabel", "Färgskala från lågt till högt")}>
                                            <span className="map-layers-panel__legend-label">{t("map.layers.metadataLow", "Lågt")}</span>
                                            <div className="map-layers-panel__legend-strip">
                                                {LEGEND_LEVEL_COLORS.map((color, i) => (
                                                    <span key={i} className="map-layers-panel__legend-strip-segment" style={{ background: color }} />
                                                ))}
                                            </div>
                                            <span className="map-layers-panel__legend-label">{t("map.layers.metadataHigh", "Högt")}</span>
                                            <span className="map-layers-panel__legend-minmax">
                                                {electoralMetadataMinMax.min.toLocaleString(i18n.language === "sv" ? "sv-SE" : "en-GB", { maximumFractionDigits: 1 })}
                                                {" – "}
                                                {electoralMetadataMinMax.max.toLocaleString(i18n.language === "sv" ? "sv-SE" : "en-GB", { maximumFractionDigits: 1 })}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
                </div>
            )}
        </div>
    );
};
